import React from 'react';
import {
  BookOpenText,
  Bot,
  BotMessageSquare,
  Brain,
  BrainCircuit,
  BriefcaseMedical,
  ChartNoAxesCombined,
  ChartPie,
  ClipboardPlus,
  FileText,
  FlaskConical,
  Hospital,
  Layers,
  LibraryBig,
  type LucideIcon,
  MapPin,
  MessageCirclePlus,
  Microscope,
  Phone,
  Search,
  Sparkles,
  Stethoscope,
  UserSearch,
  Users,
  Zap,
} from 'lucide-react';
import styled from '@emotion/styled';
import { ChartIcon } from './ChartIcon';
import { DirectoryIcon } from './DirectoryIcon';
import { InsightsIcon } from './InsightsIcon';
import { LiteratureIcon } from './LiteratureIcon';
import { QuestionBankAltIcon } from './QuestionBankAltIcon';
import { QuestionBankIcon } from './QuestionBankIcon';
import { ScheduleIcon } from './ScheduleIcon';
import { ThinkingIcon } from './ThinkingIcon';

enum LandingCardIcon {
  QuestionBankAltIcon = 'question-bank-alt-icon',
  ScheduleIcon = 'schedule-icon',
  InsightsIcon = 'insights-icon',
  ChartIcon = 'chart-icon',
  DirectoryIcon = 'directory-icon',
  QuestionBankIcon = 'question-bank-icon',
  LiteratureIcon = 'literature-icon',
  ThinkingIcon = 'thinking-icon',
  Search = 'search',
  Users = 'users',
  MapPin = 'map-pin',
  Zap = 'zap',
  Phone = 'phone',
  Brain = 'brain',
  ChartPie = 'chart-pie',
  ChartNoAxesCombined = 'chart-no-axes-combined',
  MessageCirclePlus = 'message-circle-plus',
  Stethoscope = 'stethoscope',
  UserSearch = 'user-search',
  Microscope = 'microscope',
  FileText = 'file-text',
  Sparkles = 'sparkles',
  ClipboardPlus = 'clipboard-plus',
  Layers = 'layers',
  FlaskConical = 'flask-conical',
  Bot = 'bot',
  BotMessageSquare = 'bot-message-square',
  BrainCircuit = 'brain-circuit',
  BookOpenText = 'book-open-text',
  LibraryBig = 'library-big',
  Hospital = 'hospital',
  BriefcaseMedical = 'briefcase-medical',
}

const StyledQuestionBankAltIcon = styled(QuestionBankAltIcon)(({ theme }) => ({
  color: theme.isTrinityThemed
    ? (theme.palette.sentiment?.primary?.main ?? theme.palette.primary.main)
    : theme.palette.primary.main,
  fill: theme.isTrinityThemed
    ? (theme.palette.sentiment?.primary?.main ?? theme.palette.primary.main)
    : theme.palette.primary.main,
}));

const StyledScheduleIcon = styled(ScheduleIcon)(({ theme }) => ({
  color: theme.isTrinityThemed
    ? (theme.palette.sentiment?.primary?.main ?? theme.palette.primary.main)
    : theme.palette.primary.main,
  fill: theme.isTrinityThemed
    ? (theme.palette.sentiment?.primary?.main ?? theme.palette.primary.main)
    : theme.palette.primary.main,
}));

const StyledInsightsIcon = styled(InsightsIcon)(({ theme }) => ({
  color: theme.isTrinityThemed
    ? (theme.palette.sentiment?.primary?.main ?? theme.palette.primary.main)
    : theme.palette.primary.main,
  fill: theme.isTrinityThemed
    ? (theme.palette.sentiment?.primary?.main ?? theme.palette.primary.main)
    : theme.palette.primary.main,
}));

const StyledChartIcon = styled(ChartIcon)(({ theme }) => ({
  color: theme.isTrinityThemed
    ? (theme.palette.sentiment?.primary?.main ?? theme.palette.primary.main)
    : theme.palette.primary.main,
  fill: theme.isTrinityThemed
    ? (theme.palette.sentiment?.primary?.main ?? theme.palette.primary.main)
    : theme.palette.primary.main,
}));

const StyledDirectoryIcon = styled(DirectoryIcon)(({ theme }) => ({
  color: theme.isTrinityThemed
    ? (theme.palette.sentiment?.primary?.main ?? theme.palette.primary.main)
    : theme.palette.primary.main,
  fill: theme.isTrinityThemed
    ? (theme.palette.sentiment?.primary?.main ?? theme.palette.primary.main)
    : theme.palette.primary.main,
}));

const StyledQuestionBankIcon = styled(QuestionBankIcon)(({ theme }) => ({
  color: theme.isTrinityThemed
    ? (theme.palette.sentiment?.primary?.main ?? theme.palette.primary.main)
    : theme.palette.primary.main,
  fill: theme.isTrinityThemed
    ? (theme.palette.sentiment?.primary?.main ?? theme.palette.primary.main)
    : theme.palette.primary.main,
}));

const StyledLiteratureIcon = styled(LiteratureIcon)(({ theme }) => ({
  color: theme.isTrinityThemed
    ? (theme.palette.sentiment?.primary?.main ?? theme.palette.primary.main)
    : theme.palette.primary.main,
  fill: theme.isTrinityThemed
    ? (theme.palette.sentiment?.primary?.main ?? theme.palette.primary.main)
    : theme.palette.primary.main,
}));

const StyledThinkingIcon = styled(ThinkingIcon)(({ theme }) => ({
  color: theme.isTrinityThemed
    ? (theme.palette.sentiment?.primary?.main ?? theme.palette.primary.main)
    : theme.palette.primary.main,
  stroke: theme.isTrinityThemed
    ? (theme.palette.sentiment?.primary?.main ?? theme.palette.primary.main)
    : theme.palette.primary.main,
}));

const styledLucideIcon = (Icon: LucideIcon) =>
  styled(Icon)(({ theme }) => ({
    color: theme.isTrinityThemed
      ? (theme.palette.sentiment?.primary?.main ?? theme.palette.primary.main)
      : theme.palette.primary.main,
  }));

const StyledSearch = styledLucideIcon(Search);
const StyledUsers = styledLucideIcon(Users);
const StyledMapPin = styledLucideIcon(MapPin);
const StyledZap = styledLucideIcon(Zap);
const StyledPhone = styledLucideIcon(Phone);
const StyledBrain = styledLucideIcon(Brain);
const StyledChartPie = styledLucideIcon(ChartPie);
const StyledChartNoAxesCombined = styledLucideIcon(ChartNoAxesCombined);
const StyledMessageCirclePlus = styledLucideIcon(MessageCirclePlus);
const StyledStethoscope = styledLucideIcon(Stethoscope);
const StyledUserSearch = styledLucideIcon(UserSearch);
const StyledMicroscope = styledLucideIcon(Microscope);
const StyledFileText = styledLucideIcon(FileText);
const StyledSparkles = styledLucideIcon(Sparkles);
const StyledClipboardPlus = styledLucideIcon(ClipboardPlus);
const StyledLayers = styledLucideIcon(Layers);
const StyledFlaskConical = styledLucideIcon(FlaskConical);
const StyledBot = styledLucideIcon(Bot);
const StyledBotMessageSquare = styledLucideIcon(BotMessageSquare);
const StyledBrainCircuit = styledLucideIcon(BrainCircuit);
const StyledBookOpenText = styledLucideIcon(BookOpenText);
const StyledLibraryBig = styledLucideIcon(LibraryBig);
const StyledHospital = styledLucideIcon(Hospital);
const StyledBriefcaseMedical = styledLucideIcon(BriefcaseMedical);

type IconRendererProps = {
  icon: string;
  className?: string;
  size?: number;
  width?: number;
  height?: number;
  alt?: string;
};

const legacyIconComponentMap: Partial<
  Record<string, React.ComponentType<{ className?: string; size?: number }>>
> = {
  [LandingCardIcon.QuestionBankAltIcon]: StyledQuestionBankAltIcon,
  [LandingCardIcon.ScheduleIcon]: StyledScheduleIcon,
  [LandingCardIcon.InsightsIcon]: StyledInsightsIcon,
  [LandingCardIcon.ChartIcon]: StyledChartIcon,
  [LandingCardIcon.DirectoryIcon]: StyledDirectoryIcon,
  [LandingCardIcon.QuestionBankIcon]: StyledQuestionBankIcon,
  [LandingCardIcon.LiteratureIcon]: StyledLiteratureIcon,
  [LandingCardIcon.ThinkingIcon]: StyledThinkingIcon,
};

const lucideIconComponentMap: Partial<
  Record<string, React.ComponentType<{ className?: string; size?: number; strokeWidth?: number }>>
> = {
  [LandingCardIcon.Search]: StyledSearch,
  [LandingCardIcon.Users]: StyledUsers,
  [LandingCardIcon.MapPin]: StyledMapPin,
  [LandingCardIcon.Zap]: StyledZap,
  [LandingCardIcon.Phone]: StyledPhone,
  [LandingCardIcon.Brain]: StyledBrain,
  [LandingCardIcon.ChartPie]: StyledChartPie,
  [LandingCardIcon.ChartNoAxesCombined]: StyledChartNoAxesCombined,
  [LandingCardIcon.MessageCirclePlus]: StyledMessageCirclePlus,
  [LandingCardIcon.Stethoscope]: StyledStethoscope,
  [LandingCardIcon.UserSearch]: StyledUserSearch,
  [LandingCardIcon.Microscope]: StyledMicroscope,
  [LandingCardIcon.FileText]: StyledFileText,
  [LandingCardIcon.Sparkles]: StyledSparkles,
  [LandingCardIcon.ClipboardPlus]: StyledClipboardPlus,
  [LandingCardIcon.Layers]: StyledLayers,
  [LandingCardIcon.FlaskConical]: StyledFlaskConical,
  [LandingCardIcon.Bot]: StyledBot,
  [LandingCardIcon.BotMessageSquare]: StyledBotMessageSquare,
  [LandingCardIcon.BrainCircuit]: StyledBrainCircuit,
  [LandingCardIcon.BookOpenText]: StyledBookOpenText,
  [LandingCardIcon.LibraryBig]: StyledLibraryBig,
  [LandingCardIcon.Hospital]: StyledHospital,
  [LandingCardIcon.BriefcaseMedical]: StyledBriefcaseMedical,
};

export const LandingCardIconRenderer: React.FC<IconRendererProps> = ({
  icon,
  className,
  size = 40,
  width,
  height,
  alt = 'Landing card icon',
}) => {
  const LegacyIcon = legacyIconComponentMap[icon];
  if (LegacyIcon) {
    return <LegacyIcon className={className} size={size} />;
  }

  const LucideIcon = lucideIconComponentMap[icon];
  if (LucideIcon) {
    return <LucideIcon className={className} size={size} strokeWidth={1.5} />;
  }

  // Fallback to image for URL-based icons
  return (
    <img
      src={icon}
      alt={alt}
      className={className}
      style={{ width: width || size, height: height || size }}
    />
  );
};
